/**
 * VoiceService - Web Speech API implementation for congratulations messages
 * Provides personalized voice feedback for task completion using the free browser Speech API
 */

interface VoicePreferences {
  enabled: boolean;
  rate: number;
  pitch: number;
  volume: number;
  language: string;
}

class VoiceService {
  private static instance: VoiceService;
  private preferences!: VoicePreferences;
  private voices: SpeechSynthesisVoice[] = [];
  private isSupportedBrowser!: boolean;
  private readonly STORAGE_KEY = 'voicePreferences';

  // Short team appreciation messages for customer onboarding tasks
  private readonly messageTemplates = [
    "Great job {name}! Customer task {task} completed successfully.",
    "Nice work {name}! {task} is done for the customer.",
    "Well done {name}! You've completed {task} for our customer.",
    "Excellent {name}! Customer's {task} is now finished.",
    "Good work {name}! {task} completed successfully.",
    "Thanks {name}! Customer task {task} is complete.",
    "Perfect {name}! {task} done for the customer.",
    "Outstanding {name}! Customer's {task} is finished."
  ];

  private constructor() {
    this.isSupportedBrowser = this.checkBrowserSupport();
    this.loadPreferences();
    this.initializeVoices();
  }

  public static getInstance(): VoiceService {
    if (!VoiceService.instance) {
      VoiceService.instance = new VoiceService();
    }
    return VoiceService.instance;
  }

  private checkBrowserSupport(): boolean {
    return typeof window !== 'undefined' && 
           'speechSynthesis' in window && 
           'SpeechSynthesisUtterance' in window;
  }

  private loadPreferences(): void {
    const defaultPreferences: VoicePreferences = {
      enabled: true, // Default to enabled for better UX
      rate: 1.0,     // Natural speaking speed
      pitch: 1.0,    // Natural pitch for realistic voice
      volume: 0.8,   // Slightly lower volume for workplace
      language: 'en-US'
    };

    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      this.preferences = stored ? { ...defaultPreferences, ...JSON.parse(stored) } : defaultPreferences;
    } catch (error) {
      this.preferences = defaultPreferences;
    }
  }

  private savePreferences(): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.preferences));
    } catch (error) {
      // Failed to save preferences - continue silently
    }
  }

  private initializeVoices(): void {
    if (!this.isSupportedBrowser) return;

    const loadVoices = () => {
      this.voices = speechSynthesis.getVoices();
    };

    // Load voices immediately if available
    loadVoices();

    // Also listen for voices changed event (some browsers load voices asynchronously)
    if ('onvoiceschanged' in speechSynthesis) {
      speechSynthesis.onvoiceschanged = loadVoices;
    }
  }

  private getBestVoice(): SpeechSynthesisVoice | null {
    if (!this.voices.length) return null;

    // Prefer English voices
    const englishVoices = this.voices.filter(voice => 
      voice.lang.startsWith('en') && 
      (voice.lang === this.preferences.language || voice.lang.startsWith('en-US'))
    );

    if (englishVoices.length > 0) {
      // Prefer local voices for better performance and privacy
      const localEnglishVoices = englishVoices.filter(voice => voice.localService);
      
      if (localEnglishVoices.length > 0) {
        // Prefer natural-sounding names (avoiding robotic ones)
        const naturalVoices = localEnglishVoices.filter(voice => 
          !voice.name.toLowerCase().includes('robot') && 
          !voice.name.toLowerCase().includes('synthetic') &&
          (voice.name.toLowerCase().includes('natural') || 
           voice.name.toLowerCase().includes('enhanced') ||
           voice.name.toLowerCase().includes('neural') ||
           voice.name.includes('Samantha') ||
           voice.name.includes('Alex') ||
           voice.name.includes('Victoria'))
        );
        
        return naturalVoices.length > 0 ? naturalVoices[0] : localEnglishVoices[0];
      }
      
      return englishVoices[0];
    }

    // Fallback to any available voice
    return this.voices[0];
  }

  private generateMessage(userName: string, taskTitle: string): string {
    // Select a random template for variety
    const template = this.messageTemplates[Math.floor(Math.random() * this.messageTemplates.length)];
    
    // Replace placeholders with actual values
    return template
      .replace('{name}', userName || 'there')
      .replace('{task}', taskTitle || 'the task');
  }

  public speakCongratulations(userName: string, taskTitle: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if voice is supported and enabled
      if (!this.isSupportedBrowser) {
        resolve();
        return;
      }

      if (!this.preferences.enabled) {
        resolve();
        return;
      }

      try {
        // Create the congratulations message
        const message = this.generateMessage(userName, taskTitle);
        const utterance = new SpeechSynthesisUtterance(message);

        // Configure voice properties
        utterance.rate = this.preferences.rate;
        utterance.pitch = this.preferences.pitch;
        utterance.volume = this.preferences.volume;
        utterance.lang = this.preferences.language;

        // Set the best available voice
        const voice = this.getBestVoice();
        if (voice) {
          utterance.voice = voice;
        }

        // Set up event handlers
        utterance.onend = () => {
          resolve();
        };

        utterance.onerror = (event) => {
          resolve(); // Don't reject, just resolve silently
        };

        utterance.onstart = () => {
          // Voice congratulations started
        };

        // Speak the message
        speechSynthesis.speak(utterance);

      } catch (error) {
        resolve(); // Don't reject, just resolve silently
      }
    });
  }

  public toggleEnabled(): boolean {
    this.preferences.enabled = !this.preferences.enabled;
    this.savePreferences();
    return this.preferences.enabled;
  }

  public isEnabled(): boolean {
    return this.preferences.enabled && this.isSupportedBrowser;
  }

  public setEnabled(enabled: boolean): void {
    this.preferences.enabled = enabled;
    this.savePreferences();
  }

  public getPreferences(): VoicePreferences {
    return { ...this.preferences };
  }

  public updatePreferences(updates: Partial<VoicePreferences>): void {
    this.preferences = { ...this.preferences, ...updates };
    this.savePreferences();
  }

  public testVoice(): void {
    if (this.preferences.enabled) {
      this.speakCongratulations('User', 'Test Message');
    }
  }

  public stopSpeaking(): void {
    if (this.isSupportedBrowser && speechSynthesis.speaking) {
      speechSynthesis.cancel();
    }
  }

  public getAvailableVoices(): SpeechSynthesisVoice[] {
    return [...this.voices];
  }

  public isSupported(): boolean {
    return this.isSupportedBrowser;
  }
}

// Export singleton instance
export const voiceService = VoiceService.getInstance();
export default voiceService;